<template>
  <div class="cpf-view">
    <Loading :isActive="loading" />

    <!-- Header sticky -->
    <div class="form-header">
      <div class="form-header-left">
        <button class="btn-back" @click="goBack" title="Voltar">
          <fa-icon icon="arrow-left" />
        </button>
        <div class="form-header-info">
          <div class="form-breadcrumb">{{ breadcrumb }}</div>
          <h2 class="form-title">{{ isEditMode ? `Editar ${entityName}` : `Nova ${entityName}` }}</h2>
        </div>
      </div>
      <div class="form-header-actions">
        <CButton variant="outline" color="secondary" @click="goBack">
          Cancelar
        </CButton>
        <CButton
          v-if="iaEndpoint"
          class="btn-ia"
          variant="outline"
          @click="openIaInput"
          :disabled="loadingIA"
        >
          <fa-icon icon="magic" />
          {{ loadingIA ? 'Gerando...' : 'Gerar com IA' }}
        </CButton>
        <CButton color="primary" @click="handleSubmit" :disabled="!canSubmit">
          <fa-icon :icon="isEditMode ? 'save' : 'plus'" />
          {{ isEditMode ? 'Salvar Alterações' : `Criar ${entityName}` }}
        </CButton>
      </div>
    </div>

    <!-- Main Content -->
    <div class="form-body">
      <div class="form-layout">
        <!-- Left: Main content -->
        <div class="form-main">
          <div class="form-card">
            <div class="form-group">
              <label class="field-label" for="titulo">
                Título <span class="required">*</span>
              </label>
              <input
                id="titulo"
                class="form-control form-control-lg"
                v-model="formData.titulo"
                :placeholder="`Digite o título da ${entityNameLower}`"
              />
              <div class="field-error" v-if="errors.titulo">{{ errors.titulo }}</div>
            </div>

            <div class="form-group">
              <label class="field-label" for="descricao">
                Descrição Resumida <span class="required">*</span>
              </label>
              <textarea
                id="descricao"
                class="form-control"
                v-model="formData.descricao"
                rows="3"
                :placeholder="`Breve descrição para listagens e previews`"
              />
              <div class="field-error" v-if="errors.descricao">{{ errors.descricao }}</div>
            </div>
          </div>

          <div class="form-card">
            <div class="form-group">
              <label class="field-label">
                Conteúdo <span class="required">*</span>
              </label>
              <div class="editor-wrapper">
                <editor-conteudo :item="formData" />
              </div>
              <div class="field-error" v-if="errors.conteudo">{{ errors.conteudo }}</div>
            </div>
          </div>
        </div>

        <!-- Right: Sidebar -->
        <div class="form-sidebar">
          <!-- Publicação -->
          <div class="sidebar-card">
            <div class="sidebar-card-title">Publicação</div>
            <div class="form-group">
              <label class="field-label">Status</label>
              <div class="toggle-row">
                <label class="toggle-item" :class="{ active: formData.ativo == 1 }" @click="formData.ativo = 1">
                  <input type="radio" :value="1" v-model.number="formData.ativo" />
                  Ativo
                </label>
                <label class="toggle-item" :class="{ active: formData.ativo == 0 }" @click="formData.ativo = 0">
                  <input type="radio" :value="0" v-model.number="formData.ativo" />
                  Inativo
                </label>
              </div>
            </div>
            <div class="form-group">
              <label class="field-label" for="datahora_publicacao">Data de Publicação</label>
              <datetime
                inputId="datahora_publicacao"
                input-class="form-control"
                type="datetime"
                v-model="formData.datahora_publicacao"
                :phrases="{ ok: 'Confirmar', cancel: 'Fechar' }"
                value-zone="local"
                placeholder="Selecione a data"
              />
            </div>
          </div>

          <!-- Imagem -->
          <div class="sidebar-card">
            <div class="sidebar-card-title">Imagem de Destaque</div>
            <div class="form-group">
              <image-picker
                placeholder="URL da imagem ou faça upload"
                v-model="formData.imagem_principal"
              />
            </div>
          </div>

          <!-- SEO -->
          <div class="sidebar-card">
            <div class="sidebar-card-title">
              <fa-icon icon="search" class="title-icon" />
              SEO
            </div>
            <div class="info-alert">
              <fa-icon icon="info-circle" class="info-icon" />
              <span>Configure os meta dados para melhorar o posicionamento nos buscadores.</span>
            </div>
            <div class="form-group">
              <label class="field-label" for="meta_title">Meta Título</label>
              <input
                id="meta_title"
                class="form-control"
                v-model="formData.meta_title_tag"
                placeholder="Título para buscadores"
                maxlength="60"
              />
              <div class="field-meta">
                <span class="char-counter" :class="{ 'char-warn': (formData.meta_title_tag || '').length > 55 }">
                  {{ (formData.meta_title_tag || '').length }}/60
                </span>
              </div>
            </div>
            <div class="form-group">
              <label class="field-label" for="meta_description">Meta Descrição</label>
              <textarea
                id="meta_description"
                class="form-control"
                v-model="formData.meta_description"
                rows="3"
                placeholder="Descrição para resultados de busca"
                maxlength="160"
              />
              <div class="field-meta">
                <span class="char-counter" :class="{ 'char-warn': (formData.meta_description || '').length > 150 }">
                  {{ (formData.meta_description || '').length }}/160
                </span>
              </div>
            </div>
            <div class="seo-preview">
              <div class="seo-preview-label">Preview do Google</div>
              <div class="seo-preview-box">
                <div class="seo-url">www.seusite.com.br</div>
                <div class="seo-title">{{ formData.meta_title_tag || formData.titulo || `Título da ${entityName}` }}</div>
                <div class="seo-description">{{ formData.meta_description || formData.descricao || 'Descrição aparecerá aqui...' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Input do tema IA -->
    <modal name="iaInputModal" height="auto" width="520" :scrollable="true" :clickToClose="!loadingIA">
      <div class="ia-modal">
        <div class="ia-modal-header">
          <div class="ia-modal-icon">
            <fa-icon icon="magic" />
          </div>
          <h3 class="ia-modal-title">Gerar {{ entityName }} com IA</h3>
          <p class="ia-modal-desc">Descreva o tema e a IA gerará título, descrição, conteúdo HTML e tags SEO para você revisar.</p>
        </div>
        <div class="ia-modal-body">
          <div class="form-group">
            <label class="field-label" for="ia-tema">Tema <span class="required">*</span></label>
            <input
              id="ia-tema"
              class="form-control"
              v-model="iaTema"
              :placeholder="iaPlaceholder"
              @keyup.enter="gerarComIA"
              :disabled="loadingIA"
            />
          </div>
          <div class="form-group">
            <label class="field-label" for="ia-contexto">Contexto Adicional <span class="field-optional">(opcional)</span></label>
            <textarea
              id="ia-contexto"
              class="form-control"
              v-model="iaContexto"
              rows="3"
              placeholder="Informações adicionais (ex: tom de voz, público-alvo, detalhes específicos)"
              :disabled="loadingIA"
            />
          </div>
        </div>
        <div class="modal-footer-bar">
          <CButton variant="outline" color="secondary" @click="$modal.hide('iaInputModal')" :disabled="loadingIA">
            Cancelar
          </CButton>
          <CButton color="primary" @click="gerarComIA" :disabled="!iaTema.trim() || loadingIA">
            <fa-icon :icon="loadingIA ? 'spinner' : 'magic'" :spin="loadingIA" />
            {{ loadingIA ? 'Gerando...' : 'Gerar Conteúdo' }}
          </CButton>
        </div>
      </div>
    </modal>

    <!-- Modal: Diff side-by-side -->
    <modal name="iaPreviewModal" height="auto" width="1100" :scrollable="true" :clickToClose="true">
      <CloseModal :close="() => $modal.hide('iaPreviewModal')" />
      <div class="diff">
        <div class="diff-header">
          <div class="diff-header-left">
            <div class="diff-badge">
              <fa-icon icon="magic" />
            </div>
            <div>
              <div class="diff-title">Comparar Alterações</div>
              <div class="diff-sub">Selecione os campos que deseja substituir</div>
            </div>
          </div>
          <div class="diff-header-right">
            <button class="diff-link" @click="toggleAllFields(true)">Aceitar tudo</button>
            <span class="diff-sep">|</span>
            <button class="diff-link" @click="toggleAllFields(false)">Rejeitar tudo</button>
          </div>
        </div>

        <div class="diff-cols">
          <div class="diff-col-label diff-col-label--old">
            <fa-icon icon="file-alt" class="diff-col-icon" />
            Atual
          </div>
          <div class="diff-col-label diff-col-label--new">
            <fa-icon icon="magic" class="diff-col-icon" />
            Gerado pela IA
          </div>
        </div>

        <div class="diff-body">
          <div class="diff-row" :class="{ 'diff-row--accepted': iaSelected.titulo }" v-if="iaPreview.titulo">
            <div class="diff-row-head">
              <div class="diff-row-label">Título</div>
              <button class="diff-row-btn" :class="iaSelected.titulo ? 'diff-row-btn--accept' : 'diff-row-btn--reject'" @click="toggleField('titulo')">
                <fa-icon :icon="iaSelected.titulo ? 'check' : 'times'" />
                {{ iaSelected.titulo ? 'Aceito' : 'Rejeitado' }}
              </button>
            </div>
            <div class="diff-row-pair">
              <div class="diff-cell diff-cell--old">
                <span v-if="formData.titulo">{{ formData.titulo }}</span>
                <span class="diff-empty" v-else>(vazio)</span>
              </div>
              <div class="diff-cell diff-cell--new">{{ iaPreview.titulo }}</div>
            </div>
          </div>

          <div class="diff-row" :class="{ 'diff-row--accepted': iaSelected.descricao }" v-if="iaPreview.descricao">
            <div class="diff-row-head">
              <div class="diff-row-label">Descrição</div>
              <button class="diff-row-btn" :class="iaSelected.descricao ? 'diff-row-btn--accept' : 'diff-row-btn--reject'" @click="toggleField('descricao')">
                <fa-icon :icon="iaSelected.descricao ? 'check' : 'times'" />
                {{ iaSelected.descricao ? 'Aceito' : 'Rejeitado' }}
              </button>
            </div>
            <div class="diff-row-pair">
              <div class="diff-cell diff-cell--old">
                <span v-if="formData.descricao">{{ formData.descricao }}</span>
                <span class="diff-empty" v-else>(vazio)</span>
              </div>
              <div class="diff-cell diff-cell--new">{{ iaPreview.descricao }}</div>
            </div>
          </div>

          <div class="diff-row" :class="{ 'diff-row--accepted': iaSelected.conteudo }" v-if="iaPreview.conteudo">
            <div class="diff-row-head">
              <div class="diff-row-label">Conteúdo HTML</div>
              <button class="diff-row-btn" :class="iaSelected.conteudo ? 'diff-row-btn--accept' : 'diff-row-btn--reject'" @click="toggleField('conteudo')">
                <fa-icon :icon="iaSelected.conteudo ? 'check' : 'times'" />
                {{ iaSelected.conteudo ? 'Aceito' : 'Rejeitado' }}
              </button>
            </div>
            <div class="diff-row-pair diff-row-pair--tall">
              <div class="diff-cell diff-cell--old diff-cell--html">
                <div v-if="formData.conteudo" v-html="formData.conteudo" class="diff-html-render"></div>
                <span class="diff-empty" v-else>(vazio)</span>
              </div>
              <div class="diff-cell diff-cell--new diff-cell--html">
                <div v-html="iaPreview.conteudo" class="diff-html-render"></div>
              </div>
            </div>
          </div>

          <div class="diff-divider" v-if="iaPreview.meta_title_tag || iaPreview.meta_description">
            <span>SEO</span>
          </div>

          <div class="diff-row" :class="{ 'diff-row--accepted': iaSelected.meta_title_tag }" v-if="iaPreview.meta_title_tag">
            <div class="diff-row-head">
              <div class="diff-row-label">Meta Título</div>
              <button class="diff-row-btn" :class="iaSelected.meta_title_tag ? 'diff-row-btn--accept' : 'diff-row-btn--reject'" @click="toggleField('meta_title_tag')">
                <fa-icon :icon="iaSelected.meta_title_tag ? 'check' : 'times'" />
                {{ iaSelected.meta_title_tag ? 'Aceito' : 'Rejeitado' }}
              </button>
            </div>
            <div class="diff-row-pair">
              <div class="diff-cell diff-cell--old">
                <span v-if="formData.meta_title_tag">{{ formData.meta_title_tag }}</span>
                <span class="diff-empty" v-else>(vazio)</span>
              </div>
              <div class="diff-cell diff-cell--new">{{ iaPreview.meta_title_tag }}</div>
            </div>
          </div>

          <div class="diff-row" :class="{ 'diff-row--accepted': iaSelected.meta_description }" v-if="iaPreview.meta_description">
            <div class="diff-row-head">
              <div class="diff-row-label">Meta Descrição</div>
              <button class="diff-row-btn" :class="iaSelected.meta_description ? 'diff-row-btn--accept' : 'diff-row-btn--reject'" @click="toggleField('meta_description')">
                <fa-icon :icon="iaSelected.meta_description ? 'check' : 'times'" />
                {{ iaSelected.meta_description ? 'Aceito' : 'Rejeitado' }}
              </button>
            </div>
            <div class="diff-row-pair">
              <div class="diff-cell diff-cell--old">
                <span v-if="formData.meta_description">{{ formData.meta_description }}</span>
                <span class="diff-empty" v-else>(vazio)</span>
              </div>
              <div class="diff-cell diff-cell--new">{{ iaPreview.meta_description }}</div>
            </div>
          </div>
        </div>

        <div class="diff-footer">
          <div class="diff-footer-count">{{ selectedCount }} de {{ totalCount }} campos aceitos</div>
          <div class="diff-footer-actions">
            <CButton variant="outline" color="secondary" @click="$modal.hide('iaPreviewModal')">
              Descartar
            </CButton>
            <CButton color="primary" @click="aplicarSelecaoIA" :disabled="!hasAnySelected">
              <fa-icon icon="check" />
              Aplicar Selecionados
            </CButton>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { get, post, put } from '@/helpers/apiRequest';
import CloseModal from './CloseModal';
import ImagePicker from './ImagePicker';
import EditorConteudo from './EditorConteudo';
import { validateConteudo } from '../helpers/utils';

const initialFormData = () => ({
  titulo: '',
  descricao: '',
  ativo: 1,
  conteudo: '',
  imagem_principal: '',
  meta_title_tag: '',
  meta_description: '',
  datahora_publicacao: null,
});

const initialIaSelected = () => ({
  titulo: true,
  descricao: true,
  conteudo: true,
  meta_title_tag: true,
  meta_description: true,
});

export default {
  name: 'ConteudoPageForm',
  components: {
    CloseModal,
    ImagePicker,
    EditorConteudo,
  },
  props: {
    apiBasePath: { type: String, required: true },
    entityName: { type: String, required: true },
    breadcrumb: { type: String, required: true },
    listRouteName: { type: String, required: true },
    iaEndpoint: { type: String, default: '' },
    iaPlaceholder: { type: String, default: 'Ex: Política de trocas e devoluções' },
  },
  data() {
    return {
      loading: false,
      loadingIA: false,
      formData: initialFormData(),
      errors: {},
      itemId: null,
      iaTema: '',
      iaContexto: '',
      iaPreview: {},
      iaSelected: initialIaSelected(),
    };
  },
  computed: {
    isEditMode() {
      return !!this.itemId;
    },
    entityNameLower() {
      return this.entityName.toLowerCase();
    },
    canSubmit() {
      return this.formData.titulo && this.formData.descricao;
    },
    hasAnySelected() {
      return Object.values(this.iaSelected).some(Boolean);
    },
    selectedCount() {
      const fields = ['titulo', 'descricao', 'conteudo', 'meta_title_tag', 'meta_description'];
      return fields.filter((f) => this.iaSelected[f] && this.iaPreview[f]).length;
    },
    totalCount() {
      const fields = ['titulo', 'descricao', 'conteudo', 'meta_title_tag', 'meta_description'];
      return fields.filter((f) => this.iaPreview[f]).length;
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: this.listRouteName });
    },

    loadItem() {
      if (!this.itemId) return;
      this.loading = true;
      get(`${this.apiBasePath}/${this.itemId}`)
        .then((json) => {
          this.formData = { ...initialFormData(), ...json };
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.$swal({ title: `Erro ao carregar ${this.entityNameLower}`, icon: 'error' })
            .then(() => this.goBack());
        });
    },

    validate() {
      this.errors = {};
      if (!this.formData.titulo) this.errors.titulo = 'O título é obrigatório';
      if (!this.formData.descricao) this.errors.descricao = 'A descrição é obrigatória';
      const conteudoErr = validateConteudo(this.formData.conteudo);
      if (conteudoErr) this.errors.conteudo = 'O conteúdo é obrigatório';
      return Object.keys(this.errors).length === 0;
    },

    handleSubmit() {
      if (!this.validate()) return;
      this.loading = true;

      const request = this.isEditMode
        ? put(`${this.apiBasePath}/${this.itemId}`, this.formData)
        : post(this.apiBasePath, this.formData);

      request
        .then(() => {
          this.$swal({
            title: this.isEditMode ? 'Salvo com sucesso' : 'Criado com sucesso',
            text: this.isEditMode ? `${this.entityName} atualizada!` : `${this.entityName} criada!`,
            icon: 'success',
          }).then(() => this.goBack());
        })
        .catch((error) => {
          this.loading = false;
          this.$swal({
            title: this.isEditMode ? 'Falha ao salvar' : 'Falha ao criar',
            text: error.message,
            icon: 'error',
          });
        });
    },

    // IA
    openIaInput() {
      this.iaTema = '';
      this.iaContexto = '';
      this.$modal.show('iaInputModal');
    },

    gerarComIA() {
      if (!this.iaTema.trim() || this.loadingIA || !this.iaEndpoint) return;
      this.loadingIA = true;

      post(this.iaEndpoint, {
        tema: this.iaTema,
        contexto_adicional: this.iaContexto,
      })
        .then((json) => {
          this.loadingIA = false;
          this.$modal.hide('iaInputModal');

          const dados = json.data || json.message || json;
          if (typeof dados === 'string' || !dados) {
            this.$swal({ title: 'Resposta inesperada da IA', icon: 'warning' });
            return;
          }

          this.iaPreview = { ...dados };
          this.iaSelected = initialIaSelected();
          this.$modal.show('iaPreviewModal');
        })
        .catch((error) => {
          this.loadingIA = false;
          this.$swal({
            title: 'Falha ao gerar conteúdo',
            text: error.message || 'Erro ao se comunicar com o serviço de IA',
            icon: 'error',
          });
        });
    },

    toggleField(field) {
      this.iaSelected[field] = !this.iaSelected[field];
    },
    toggleAllFields(value) {
      const fields = ['titulo', 'descricao', 'conteudo', 'meta_title_tag', 'meta_description'];
      fields.forEach((f) => { this.iaSelected[f] = value; });
    },

    aplicarSelecaoIA() {
      const campos = ['titulo', 'descricao', 'conteudo', 'meta_title_tag', 'meta_description'];
      campos.forEach((campo) => {
        if (this.iaSelected[campo] && this.iaPreview[campo]) {
          this.formData[campo] = this.iaPreview[campo];
        }
      });

      this.$modal.hide('iaPreviewModal');

      const qtd = campos.filter((c) => this.iaSelected[c] && this.iaPreview[c]).length;
      this.$swal({
        title: `${qtd} campo${qtd > 1 ? 's' : ''} aplicado${qtd > 1 ? 's' : ''}`,
        text: 'Revise o conteúdo e ajuste o que for necessário.',
        icon: 'success',
        timer: 2500,
      });
    },
  },
  beforeMount() {
    this.itemId = this.$route.params.id || null;
    if (this.itemId) {
      this.loadItem();
    }
  },
};
</script>

<style scoped>
.cpf-view {
  min-height: 100vh;
  background: #f4f5f7;
}

/* ====== Header ====== */
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid #e4e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.form-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.btn-back {
  width: 36px;
  height: 36px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #667085;
  transition: all 0.15s;
}

.btn-back:hover {
  background: #f9fafb;
  border-color: #d0d5dd;
  color: #344054;
}

.form-breadcrumb {
  font-size: 11px;
  color: #98a2b3;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.form-title {
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
  margin: 2px 0 0;
}

.form-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-ia {
  color: #7c3aed !important;
  border-color: #c4b5fd !important;
  background: #f5f3ff !important;
}

.btn-ia:hover {
  background: #ede9fe !important;
  border-color: #a78bfa !important;
}

/* ====== Body ====== */
.form-body {
  padding: 24px;
}

.form-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
}

/* ====== Cards ====== */
.form-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 20px;
}

.sidebar-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
}

.sidebar-card-title {
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.title-icon { font-size: 12px; color: #667085; }

/* ====== Form Fields ====== */
.form-group { margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 5px;
}

.required { color: #e74c3c; margin-left: 2px; }
.field-optional { font-weight: 400; color: #98a2b3; font-size: 11px; }
.field-error { color: #b42318; font-size: 12px; margin-top: 4px; }
.field-meta { display: flex; justify-content: flex-end; margin-top: 4px; }

.form-control-lg {
  font-size: 16px;
  padding: 10px 14px;
  font-weight: 500;
}

/* ====== Toggle Row ====== */
.toggle-row { display: flex; gap: 8px; }

.toggle-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #667085;
  transition: all 0.15s;
}

.toggle-item input { display: none; }
.toggle-item:hover { border-color: #d0d5dd; background: #f9fafb; }
.toggle-item.active { border-color: #3a3e81; background: #f2f2f8; color: #3a3e81; font-weight: 600; }

/* ====== Counters ====== */
.char-counter { font-size: 11px; color: #98a2b3; font-weight: 500; }
.char-warn { color: #dc6803; }

/* ====== Info Alert ====== */
.info-alert {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; background: #eff8ff; border: 1px solid #b2ddff;
  border-radius: 8px; font-size: 12px; color: #1849a9; margin-bottom: 16px; line-height: 1.4;
}
.info-icon { color: #2e90fa; font-size: 14px; flex-shrink: 0; margin-top: 1px; }

/* ====== SEO Preview ====== */
.seo-preview { margin-top: 4px; }
.seo-preview-label { font-size: 10px; font-weight: 700; color: #98a2b3; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
.seo-preview-box { background: #f9fafb; padding: 12px; border-radius: 8px; border: 1px solid #e9ecef; }
.seo-url { font-size: 11px; color: #5f6368; margin-bottom: 3px; }
.seo-title { font-size: 15px; color: #1a0dab; font-weight: 400; margin-bottom: 3px; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.seo-description { font-size: 12px; color: #4d5156; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* ====== Editor ====== */
.editor-wrapper { border: 1px solid #e4e7eb; border-radius: 8px; overflow: hidden; min-height: 400px; }

/* ====== IA Input Modal ====== */
.ia-modal { padding: 0; }
.ia-modal-header { padding: 28px 24px 0; text-align: center; }
.ia-modal-icon { width: 48px; height: 48px; border-radius: 14px; background: linear-gradient(135deg, #f5f3ff, #ede9fe); display: flex; align-items: center; justify-content: center; font-size: 20px; color: #7c3aed; margin: 0 auto 16px; }
.ia-modal-title { font-size: 18px; font-weight: 700; color: #1d2939; margin: 0 0 8px; }
.ia-modal-desc { font-size: 13px; color: #667085; line-height: 1.5; margin: 0; }
.ia-modal-body { padding: 24px; }

/* ====== Diff Preview Modal ====== */
.diff { padding: 0; background: #fff; }

.diff-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-bottom: 1px solid #e4e7eb; }
.diff-header-left { display: flex; align-items: center; gap: 12px; }
.diff-badge { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, #7c3aed, #6d28d9); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; flex-shrink: 0; }
.diff-title { font-size: 15px; font-weight: 700; color: #1d2939; }
.diff-sub { font-size: 12px; color: #98a2b3; margin-top: 1px; }
.diff-header-right { display: flex; align-items: center; gap: 6px; }
.diff-link { background: none; border: none; font-size: 12px; color: #3a3e81; font-weight: 600; cursor: pointer; padding: 2px 4px; }
.diff-link:hover { text-decoration: underline; }
.diff-sep { color: #d0d5dd; font-size: 12px; }

.diff-cols { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #e4e7eb; }
.diff-col-label { display: flex; align-items: center; gap: 6px; padding: 8px 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
.diff-col-icon { font-size: 11px; }
.diff-col-label--old { color: #98a2b3; background: #fafbfc; border-right: 1px solid #e4e7eb; }
.diff-col-label--new { color: #7c3aed; background: #faf5ff; }

.diff-body { max-height: 56vh; overflow-y: auto; }
.diff-row { border-bottom: 1px solid #e4e7eb; }
.diff-row:last-child { border-bottom: none; }
.diff-row-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 20px; background: #f9fafb; border-bottom: 1px solid #f0f1f3; }
.diff-row-label { font-size: 11px; font-weight: 700; color: #667085; text-transform: uppercase; letter-spacing: 0.3px; }

.diff-row-btn { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; border: 1px solid; cursor: pointer; transition: all 0.15s; }
.diff-row-btn--accept { background: #ecfdf3; border-color: #a6f4c5; color: #067647; }
.diff-row-btn--accept:hover { background: #d1fadf; }
.diff-row-btn--reject { background: #fef3f2; border-color: #fecdca; color: #b42318; }
.diff-row-btn--reject:hover { background: #fee4e2; }

.diff-row-pair { display: grid; grid-template-columns: 1fr 1fr; min-height: 48px; }
.diff-row-pair--tall { min-height: 120px; }
.diff-cell { padding: 12px 20px; font-size: 13px; line-height: 1.6; color: #344054; word-break: break-word; }
.diff-cell--old { background: #fffbfa; border-right: 1px solid #e4e7eb; color: #667085; }
.diff-cell--new { background: #f6fef9; }
.diff-row--accepted .diff-cell--old { background: #fef3f2; text-decoration: line-through; text-decoration-color: #fecdca; color: #98a2b3; }
.diff-row--accepted .diff-cell--new { background: #ecfdf3; }

.diff-cell--html { max-height: 240px; overflow-y: auto; }
.diff-html-render { font-size: 13px; line-height: 1.65; }
.diff-html-render >>> h2 { font-size: 16px; font-weight: 700; color: #1d2939; margin: 12px 0 6px; }
.diff-html-render >>> h3 { font-size: 14px; font-weight: 600; color: #344054; margin: 10px 0 4px; }
.diff-html-render >>> p { margin: 0 0 8px; }
.diff-html-render >>> ul, .diff-html-render >>> ol { margin: 0 0 8px; padding-left: 20px; }
.diff-html-render >>> li { margin-bottom: 3px; }
.diff-html-render >>> strong { color: #1d2939; }
.diff-empty { color: #d0d5dd; font-style: italic; font-size: 12px; }

.diff-divider { display: flex; align-items: center; gap: 10px; padding: 6px 20px; background: #f9fafb; border-bottom: 1px solid #e4e7eb; }
.diff-divider span { font-size: 10px; font-weight: 700; color: #98a2b3; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
.diff-divider::after { content: ''; flex: 1; height: 1px; background: #e4e7eb; }

.diff-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; border-top: 1px solid #e4e7eb; background: #f9fafb; }
.diff-footer-count { font-size: 12px; color: #667085; font-weight: 500; }
.diff-footer-actions { display: flex; gap: 10px; }

/* ====== Modal Footer ====== */
.modal-footer-bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 24px; border-top: 1px solid #e9ecef; }
.ia-modal .modal-footer-bar { justify-content: flex-end; }

/* ====== Responsive ====== */
@media (max-width: 1024px) {
  .form-layout { grid-template-columns: 1fr; }
  .form-sidebar { order: -1; display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
  .sidebar-card { margin-bottom: 0; }
  .diff-row-pair { grid-template-columns: 1fr; }
  .diff-cols { grid-template-columns: 1fr; }
  .diff-col-label--old { border-right: none; border-bottom: 1px solid #e4e7eb; }
  .diff-cell--old { border-right: none; border-bottom: 1px solid #f0f1f3; }
}
</style>
